import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, UserCog, Calendar } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import ConciergeCockpitChips from "../components/ConciergeCockpitChips";
import ConciergeWelcomeRow from "../components/ConciergeWelcomeRow";
import ConciergeProjectsBar from "../components/ConciergeProjectsBar";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * ConciergeHome — schlanker Concierge-Startbildschirm.
 *
 * Feb 2026 · Auf User-Directive „concierge Ebene: Nur Kalender, Objekte,
 * Profil und seine zugewiesene Listen wie schon installiert."
 *
 * Wird auf `/team` gerendert, sobald `effectiveRole === "concierge"`
 * (echter Concierge-Login ODER Admin-Impersonation eines Concierges).
 * Zeigt:
 *   1. Anreise-Radar — die nächsten 3 Anreisen mit Wetter
 *   2. Objekte-Übersicht (kurz) — Anzahl zugewiesener Objekte + Kachel-
 *      Link zu /team/properties
 *   3. Direkt-Zugriff auf Kalender + Profil
 */
export default function ConciergeHome() {
  const { user, activeConciergeId, activeConciergeName } = useAuth();
  const { t } = useI18n();
  const [properties, setProperties] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team/properties");
        // Feb 2026 · Client-Filter für Admin-Impersonation:
        // Ein realer Concierge sieht via Server-Scope bereits nur seine
        // Objekte. Ein Admin bekommt vom Server ALLE — hier klemmen wir
        // auf `activeConciergeId`, damit die Concierge-Perspektive
        // konsistent ist (Zahl der Objekte, Handlungs-Badges etc.).
        let rows = Array.isArray(data) ? data : [];
        if (activeConciergeId && user?.role === "admin") {
          rows = rows.filter((p) => p.assigned_concierge_id === activeConciergeId);
        }
        setProperties(rows);
      } catch (e) {
        console.warn("concierge home properties fetch failed:", e);
        setProperties([]);
      }
    })();
  }, [user?.id, user?.role, activeConciergeId, activeConciergeName]);

  const displayName = activeConciergeName || user?.name || "Concierge";
  const propCount = properties?.length ?? null;
  // 24 Jun 2026 · Ampel auf der Objekte-Kachel: statt nur der roten
  // Handlungsbedarf-Zahl zeigen wir die volle Status-Verteilung (rot/blau/
  // grün) aus `status_color`, damit die Startseite auf einen Blick den
  // Zustand aller zugewiesenen Objekte signalisiert.
  const statusCounts = (properties || []).reduce((acc, p) => {
    const k = p.status_color || "grey";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:py-12"
      data-testid="concierge-home"
    >
      {/* --- Kopfzeile ------------------------------------------------- */}
      <header className="mb-8">
        <div className="label-caps mb-2">Weissblom · Concierge</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy">
          Guten Tag, {displayName}
        </h1>
        <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl">
          Deine Übersicht für heute — deine offenen Aufgaben, deine nächste
          Abreise und Anreise, deine Projekte.
        </p>
      </header>

      {/* --- Kompakte Puls-Zeile (Heute · Diese Woche) ----------------- */}
      <ConciergeWelcomeRow />

      {/* --- Cockpit-Chips (3 Kacheln · Aufgaben / Abreise / Anreise) --- */}
      <ConciergeCockpitChips />

      {/* --- Voll-breiter Projekte-Balken direkt unter den Kacheln ------
          Feb 2026 · Der frühere Abreise-Radar-Balken ist entfernt — die
          Info „nächste Abreise/Anreise" steht bereits in den 3 Kacheln
          oben; ein zweiter Balken wäre redundant. */}
      <ConciergeProjectsBar />

      {/* --- Schnellzugriff-Karten ------------------------------------- */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        data-testid="concierge-quick-cards"
      >
        <QuickCard
          to="/team/calendar"
          icon={Calendar}
          label="Kalender"
          hint="Alle Abwesenheiten, Anfragen und Termine — inklusive Absence-Bars für deine Objekte."
          testid="concierge-quick-calendar"
        />
        <QuickCard
          to="/team/properties"
          icon={Building2}
          label="Objekte"
          hint={
            propCount === null
              ? "Wird geladen …"
              : `${propCount} ${propCount === 1 ? "Objekt zugewiesen" : "Objekte zugewiesen"}`
          }
          testid="concierge-quick-properties"
          statusCounts={statusCounts}
        />
        <QuickCard
          to="/team/profile"
          icon={UserCog}
          label="Mein Profil"
          hint="Name · Profilfoto · Telegram-Push"
          testid="concierge-quick-profile"
        />
      </div>

    </div>
  );
}

const OBJEKTE_STATUS_META = [
  { key: "red", color: STATUS_COLORS.red, label: "Handlungsbedarf" },
  { key: "blue", color: STATUS_COLORS.blue, label: "Geplant" },
  { key: "green", color: STATUS_COLORS.green, label: "Erledigt" },
];

function QuickCard({ to, icon: Icon, label, hint, testid, statusCounts = null }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="bg-white border border-navy/10 p-5 flex flex-col gap-3 hover:border-gold transition-colors group"
    >
      <div className="flex items-center justify-between">
        <div
          className="w-10 h-10 flex items-center justify-center"
          style={{ background: withAlpha(BRAND_COLORS.navy, 0.06) }}
        >
          <Icon size={20} strokeWidth={1.6} style={{ color: BRAND_COLORS.navy }} />
        </div>
        {statusCounts && (
          <div
            className="flex items-center gap-1.5 flex-wrap justify-end"
            data-testid="concierge-objekte-status-dots"
          >
            {OBJEKTE_STATUS_META.filter((m) => (statusCounts[m.key] || 0) > 0).map((m) => (
              <span
                key={m.key}
                className="inline-flex items-center text-[10.5px] tracking-[0.1em] uppercase px-2 py-0.5 rounded-full font-semibold"
                style={{ background: m.color, color: BRAND_COLORS.ivory }}
                title={`${statusCounts[m.key]} × ${m.label}`}
                data-testid={`concierge-objekte-status-${m.key}`}
              >
                {statusCounts[m.key]}
              </span>
            ))}
          </div>
        )}
      </div>
      <div>
        <div className="font-display text-xl text-navy group-hover:text-gold transition-colors">
          {label}
        </div>
        <div className="text-[12.5px] text-navy-dark/60 mt-1 leading-relaxed">
          {hint}
        </div>
      </div>
    </Link>
  );
}
